/**
 * useLeadBoard — Zustand-Store fuer das Kanban-Board.
 *
 * EIN Store fuer den gesamten Board-State:
 * - leads, users, filters
 * - selectedLeadId + isModalOpen (Card-Detail-Modal — Plan 02)
 * - isCreateModalOpen (Create-Lead-Modal — Plan 03)
 * - selectedLeadIds + selectMode (Multi-Select Bulk-Actions — Plan 04)
 * - Polling alle 30s, pausiert wenn Modal offen ODER Bulk-Aktion laeuft
 *
 * Pattern:
 * - Init laedt Leads + Users parallel und startet Polling
 * - moveLead nutzt Optimistic Update + Rollback bei API-Fehler (BOARD-02)
 * - Mutations (create/update/delete/tags) updaten leads direkt nach API-Erfolg
 *
 * Keine Logger-Aufrufe (Frontend), keine direkten URL-Strings — alles via leadsApi.
 */

import { create } from 'zustand';
import type { BoardFilters, Lead, LeadTask, UserMini } from '../types';
import type { BulkActionRequest, BulkActionResponse } from '../services/leadsApi';
import { leadsApi } from '../services/leadsApi';

const POLL_INTERVAL_MS = 30000;

/**
 * Aggregations-Datumsformat (Berlin-naiv): heute-Ende und Woche-Ende
 * werden lokal aus new Date() abgeleitet — kein Server-Timezone-Round-Trip.
 */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
function weekEndIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

const INITIAL_FILTERS: BoardFilters = {
  search: '',
  tags: [],
  assignedUserId: null,
  dateRange: 'all',
};

interface LeadBoardState {
  // ── Daten ──
  leads: Lead[];
  users: UserMini[];

  // ── UI-State ──
  filters: BoardFilters;
  selectedLeadId: number | null;
  isModalOpen: boolean;
  isCreateModalOpen: boolean;
  loading: boolean;
  error: string | null;

  // ── Tasks (Phase 4 Plan 01) ──
  /** Tasks pro Lead (Cache fuer TasksTab + Card-Badge). Key = leadId. */
  tasksByLead: Record<number, LeadTask[]>;
  /** Anzahl offene Tasks die heute oder frueher faellig sind (board-weit). */
  taskCountToday: number;
  /** Anzahl offene Tasks die innerhalb der naechsten 7 Tage faellig sind (board-weit). */
  taskCountWeek: number;
  /** Wenn true: Board filtert auf Karten mit heute-faelliger Task. */
  taskFilterActive: boolean;

  // ── Bulk-Select (Phase 4 Plan 04 — ADMIN-03..06) ──
  /** IDs der aktuell selektierten Karten. Set<number> fuer O(1)-Lookup. */
  selectedLeadIds: Set<number>;
  /** Select-Mode: true wenn Shift-Klick Select-Modus aktiviert hat (Checkboxen permanent sichtbar). */
  selectMode: boolean;
  /** true waehrend runBulkAction laeuft — Polling wird pausiert. */
  bulkBusy: boolean;

  // ── interner Polling-Handle ──
  _pollHandle: ReturnType<typeof setInterval> | null;

  // ── Actions: Lifecycle ──
  init: () => Promise<void>;
  fetchLeads: () => Promise<void>;
  fetchUsers: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
  clearError: () => void;

  // ── Actions: UI ──
  setFilter: (patch: Partial<BoardFilters>) => void;
  resetFilters: () => void;
  selectLead: (id: number | null) => void;
  openCreateModal: () => void;
  closeCreateModal: () => void;

  // ── Mutations ──
  moveLead: (id: number, newStatus: Lead['status']) => Promise<void>;
  updateLead: (id: number, patch: Partial<Lead>) => Promise<Lead>;
  createLead: (data: Partial<Lead>) => Promise<Lead>;
  deleteLead: (id: number) => Promise<void>;
  addTag: (id: number, tag: string) => Promise<Lead>;
  removeTag: (id: number, tag: string) => Promise<Lead>;

  // ── Tasks-Actions (Phase 4 Plan 01) ──
  /** Aktuellen Tagesueberblick (today + week counts) vom Server holen. */
  fetchTaskCounts: () => Promise<void>;
  /** Tasks fuer einen einzelnen Lead nachladen — fuellt tasksByLead[leadId]. */
  loadTasksForLead: (leadId: number) => Promise<void>;
  /** Tasks im board-weiten Filter ein-/ausschalten. */
  setTaskFilterActive: (active: boolean) => void;
  /** Task optimistisch anlegen (rollt bei Server-Fehler zurueck). */
  createTaskOptimistic: (
    leadId: number,
    body: { title: string; dueDate: string },
  ) => Promise<LeadTask>;
  /** Task optimistisch abhaken / oeffnen. */
  setTaskCompletedOptimistic: (
    taskId: number,
    leadId: number,
    completed: boolean,
  ) => Promise<void>;

  // ── Bulk-Select-Actions (Phase 4 Plan 04 — ADMIN-03..06) ──
  /** Fuegt Lead zu/entfernt aus selectedLeadIds (immutable Set — neues Set erstellen). */
  toggleLeadSelection: (id: number) => void;
  /** Leert Selektion und deaktiviert Select-Mode. */
  clearSelection: () => void;
  /** Toggled selectMode (Shift-Klick aktiviert permanente Checkbox-Sichtbarkeit). */
  toggleSelectMode: () => void;
  /**
   * Fuehrt Bulk-Aktion via Backend aus.
   * Bei Erfolg: clearSelection() + fetchLeads() + return result.
   * Bei HTTP-Fehler: Promise.reject mit Fehler-Nachricht.
   */
  runBulkAction: (req: BulkActionRequest) => Promise<BulkActionResponse>;
}

export const useLeadBoard = create<LeadBoardState>((set, get) => ({
  leads: [],
  users: [],
  filters: INITIAL_FILTERS,
  selectedLeadId: null,
  isModalOpen: false,
  isCreateModalOpen: false,
  loading: false,
  error: null,
  tasksByLead: {},
  taskCountToday: 0,
  taskCountWeek: 0,
  taskFilterActive: false,
  selectedLeadIds: new Set<number>(),
  selectMode: false,
  bulkBusy: false,
  _pollHandle: null,

  init: async () => {
    set({ loading: true, error: null });
    try {
      const [leads, users] = await Promise.all([
        leadsApi.list(),
        leadsApi.listUsers(),
      ]);
      set({ leads, users, loading: false });
      // Tasks-Counts non-blocking initial laden — Fehler nicht in error setzen,
      // sonst blockiert ein Tasks-Endpoint-Problem den Board-Init.
      get().fetchTaskCounts().catch(() => undefined);
    } catch (e: any) {
      set({ error: e?.message ?? 'Init fehlgeschlagen', loading: false });
    }
    get().startPolling();
  },

  fetchLeads: async () => {
    try {
      const leads = await leadsApi.list();
      set({ leads });
    } catch (e: any) {
      set({ error: e?.message ?? 'Aktualisierung fehlgeschlagen' });
    }
  },

  fetchUsers: async () => {
    try {
      const users = await leadsApi.listUsers();
      set({ users });
    } catch (e: any) {
      set({ error: e?.message ?? 'User-Liste konnte nicht geladen werden' });
    }
  },

  startPolling: () => {
    const existing = get()._pollHandle;
    if (existing) clearInterval(existing);
    const handle = setInterval(() => {
      const s = get();
      // Polling pausiert wenn ein Modal offen ist oder Bulk-Aktion laeuft —
      // Inhalte sollen waehrend der Bearbeitung nicht unter dem Editor wegspringen.
      if (!s.isModalOpen && !s.isCreateModalOpen && !s.bulkBusy) {
        s.fetchLeads();
        s.fetchTaskCounts().catch(() => undefined);
      }
    }, POLL_INTERVAL_MS);
    set({ _pollHandle: handle });
  },

  stopPolling: () => {
    const h = get()._pollHandle;
    if (h) {
      clearInterval(h);
      set({ _pollHandle: null });
    }
  },

  clearError: () => set({ error: null }),

  setFilter: (patch) =>
    set((state) => ({ filters: { ...state.filters, ...patch } })),

  resetFilters: () => set({ filters: INITIAL_FILTERS }),

  selectLead: (id) =>
    set({ selectedLeadId: id, isModalOpen: id !== null }),

  openCreateModal: () => set({ isCreateModalOpen: true }),
  closeCreateModal: () => set({ isCreateModalOpen: false }),

  moveLead: async (id, newStatus) => {
    const prev = get().leads;
    // Optimistic Update: Karte erscheint sofort in neuer Spalte
    set({
      leads: prev.map((l) =>
        l.id === id ? { ...l, status: newStatus, autoWorkflowActive: false } : l,
      ),
    });
    try {
      const updated = await leadsApi.update(id, { status: newStatus });
      set({
        leads: get().leads.map((l) => (l.id === id ? updated : l)),
      });
    } catch (e: any) {
      // Rollback auf vorherigen State + Fehlertext fuer Toast
      set({
        leads: prev,
        error: `Verschieben fehlgeschlagen: ${e?.message ?? 'Unbekannter Fehler'}`,
      });
      throw e;
    }
  },

  updateLead: async (id, patch) => {
    const updated = await leadsApi.update(id, patch);
    set({ leads: get().leads.map((l) => (l.id === id ? updated : l)) });
    return updated;
  },

  createLead: async (data) => {
    const created = await leadsApi.create(data);
    set({ leads: [...get().leads, created] });
    return created;
  },

  deleteLead: async (id) => {
    await leadsApi.remove(id);
    set({ leads: get().leads.filter((l) => l.id !== id) });
  },

  addTag: async (id, tag) => {
    const updated = await leadsApi.addTag(id, tag);
    set({ leads: get().leads.map((l) => (l.id === id ? updated : l)) });
    return updated;
  },

  removeTag: async (id, tag) => {
    const updated = await leadsApi.removeTag(id, tag);
    set({ leads: get().leads.map((l) => (l.id === id ? updated : l)) });
    return updated;
  },

  // ── Tasks-Actions (Phase 4 Plan 01) ─────────────────────────────────
  fetchTaskCounts: async () => {
    try {
      // Backend liefert nur ein "dueBefore=<date>&completed=false"-Query —
      // wir holen die Woche-Liste (ueberlappt mit "heute"), bauen daraus
      // beide Zaehler selbst und cachen die Tasks gleich im tasksByLead.
      const weekIso = weekEndIso();
      const todayIsoStr = todayIso();
      const weekTasks = await leadsApi.tasks.listByDue(weekIso);
      const today = weekTasks.filter((t) => t.dueDate <= todayIsoStr).length;
      const week = weekTasks.length;
      // tasksByLead aus week-Aggregation befuellen (nur offene Tasks im
      // Aggregations-Fenster — fuer Card-Badge ausreichend).
      const byLead: Record<number, LeadTask[]> = { ...get().tasksByLead };
      // ueberlappende Lead-IDs zuerst leeren um veraltete Eintraege zu
      // ersetzen, dann neu befuellen.
      const touchedLeads = new Set(weekTasks.map((t) => t.leadId));
      for (const leadId of touchedLeads) {
        byLead[leadId] = weekTasks.filter((t) => t.leadId === leadId);
      }
      set({ taskCountToday: today, taskCountWeek: week, tasksByLead: byLead });
    } catch (e: any) {
      // Tasks-Fehler nicht in error setzen — Board funktioniert weiter
      // ohne Counts.
    }
  },

  loadTasksForLead: async (leadId) => {
    try {
      const tasks = await leadsApi.tasks.listForLead(leadId);
      set({ tasksByLead: { ...get().tasksByLead, [leadId]: tasks } });
    } catch (e: any) {
      set({ error: e?.message ?? 'Tasks konnten nicht geladen werden' });
    }
  },

  setTaskFilterActive: (active) => set({ taskFilterActive: active }),

  createTaskOptimistic: async (leadId, body) => {
    const prev = get().tasksByLead[leadId] ?? [];
    // Optimistic: temp-id (negativ) damit React-Key stabil ist und wir den
    // Eintrag nach Server-Response ersetzen koennen.
    const tempId = -Date.now();
    const nowIso = new Date().toISOString();
    const optimistic: LeadTask = {
      id: tempId,
      leadId,
      title: body.title,
      dueDate: body.dueDate,
      completedAt: null,
      createdByUserId: null,
      assignedUserId: null,
      createdAt: nowIso,
      updatedAt: nowIso,
    };
    set({
      tasksByLead: {
        ...get().tasksByLead,
        [leadId]: [...prev, optimistic],
      },
    });
    try {
      const created = await leadsApi.tasks.create({ leadId, ...body });
      // optimistischen Eintrag durch echten ersetzen
      set({
        tasksByLead: {
          ...get().tasksByLead,
          [leadId]: (get().tasksByLead[leadId] ?? []).map((t) =>
            t.id === tempId ? created : t,
          ),
        },
      });
      // Counts refreshen (non-blocking)
      get().fetchTaskCounts().catch(() => undefined);
      return created;
    } catch (e: any) {
      // Rollback
      set({
        tasksByLead: { ...get().tasksByLead, [leadId]: prev },
        error: e?.message ?? 'Task konnte nicht angelegt werden',
      });
      throw e;
    }
  },

  setTaskCompletedOptimistic: async (taskId, leadId, completed) => {
    const prev = get().tasksByLead[leadId] ?? [];
    const optimistic = prev.map((t) =>
      t.id === taskId
        ? { ...t, completedAt: completed ? new Date().toISOString() : null }
        : t,
    );
    set({
      tasksByLead: { ...get().tasksByLead, [leadId]: optimistic },
    });
    try {
      const updated = await leadsApi.tasks.setCompleted(taskId, completed);
      set({
        tasksByLead: {
          ...get().tasksByLead,
          [leadId]: (get().tasksByLead[leadId] ?? []).map((t) =>
            t.id === taskId ? updated : t,
          ),
        },
      });
      get().fetchTaskCounts().catch(() => undefined);
    } catch (e: any) {
      // Rollback
      set({
        tasksByLead: { ...get().tasksByLead, [leadId]: prev },
        error: e?.message ?? 'Task-Status konnte nicht geaendert werden',
      });
      throw e;
    }
  },

  // ── Bulk-Select-Actions (Phase 4 Plan 04 — ADMIN-03..06) ────────────────
  toggleLeadSelection: (id) => {
    const prev = get().selectedLeadIds;
    // Neues Set erstellen fuer Zustand-Re-Render (Set ist reference-equal wenn mutiert)
    const next = new Set(prev);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    set({ selectedLeadIds: next });
  },

  clearSelection: () => set({ selectedLeadIds: new Set<number>(), selectMode: false }),

  toggleSelectMode: () => set((s) => ({ selectMode: !s.selectMode })),

  runBulkAction: async (req) => {
    set({ bulkBusy: true });
    try {
      const result = await leadsApi.bulk.execute(req);
      get().clearSelection();
      // Board nach Bulk-Aktion neu laden (synchron warten damit UI konsistent)
      await get().fetchLeads();
      return result;
    } catch (e: any) {
      set({ error: e?.message ?? 'Bulk-Aktion fehlgeschlagen' });
      throw e;
    } finally {
      set({ bulkBusy: false });
    }
  },
}));
